import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { StickyHeader } from "@/components/site/StickyHeader";
import { Footer } from "@/components/site/Footer";
import { FadeIn } from "@/components/site/FadeIn";
import { EmailCaptureForm } from "@/components/site/EmailCaptureForm";
import { articles } from "@/lib/articles";

export const Route = createFileRoute("/f/$slug")({
  loader: ({ params }) => {
    const article = articles[params.slug];
    if (!article || article.basePath !== "/f") throw notFound();
    const { Body: _Body, ...serializableArticle } = article;
    return { article: serializableArticle };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Article not found" }, { name: "robots", content: "noindex" }],
      };
    }
    const { article } = loaderData;
    // Slugs can contain characters like "%" that must stay percent-encoded
    // in canonical/og URLs so they match the originally indexed URL.
    const url = `https://katiefernands.com/f/${encodeURIComponent(params.slug)}`;
    const imageUrl = article.image ? `https://katiefernands.com${article.image.url}` : null;
    return {
      meta: [
        { title: article.titleTag },
        { name: "description", content: article.metaDescription },
        { property: "og:title", content: article.title },
        { property: "og:description", content: article.metaDescription },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:title", content: article.title },
        { name: "twitter:description", content: article.metaDescription },
        ...(imageUrl
          ? [
              { property: "og:image", content: imageUrl },
              { name: "twitter:image", content: imageUrl },
              { name: "twitter:card", content: "summary_large_image" },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.metaDescription,
            datePublished: article.date,
            dateModified: article.date,
            author: {
              "@type": "Person",
              name: "Katie Fernands",
              url: "https://www.linkedin.com/in/katiefernands/",
            },
            publisher: {
              "@type": "Person",
              name: "Katie Fernands",
              url: "https://katiefernands.com",
            },
            mainEntityOfPage: url,
            ...(imageUrl ? { image: imageUrl } : {}),
            keywords: article.keywords.join(", "),
          }),
        },
        ...(article.faq
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: article.faq.map((q) => ({
                    "@type": "Question",
                    name: q.question,
                    acceptedAnswer: { "@type": "Answer", text: q.answer },
                  })),
                }),
              },
            ]
          : []),
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-6">
      <div className="text-center">
        <h1 className="text-3xl font-serif">Article not found</h1>
        <Link to="/blog" className="text-primary hover:underline mt-4 inline-block">
          ← Back to all articles
        </Link>
      </div>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-6">
      <p>Something went wrong: {error.message}</p>
    </div>
  ),
  component: LegacyArticlePage,
});

function LegacyArticlePage() {
  const { article } = Route.useLoaderData();
  const Body = articles[article.slug].Body;
  return (
    <div className="bg-background text-foreground min-h-screen flex flex-col">
      <StickyHeader />
      <main className="flex-1 pt-32 pb-24">
        <article className="mx-auto max-w-3xl px-6 lg:px-10">
          <FadeIn>
            <Link to="/blog" className="text-xs uppercase tracking-[0.24em] text-primary">
              ← Insights
            </Link>
            <h1 className="mt-6 text-3xl md:text-4xl font-sans font-semibold tracking-tight leading-[1.2]">
              {article.title}
            </h1>
            <p className="mt-4 text-sm text-foreground/60">
              By Katie Fernands ·{" "}
              {new Date(article.date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
                timeZone: "UTC",
              })}{" "}
              · {article.readingTime}
            </p>
            {article.image ? (
              <img
                src={article.image.url}
                alt={article.image.alt}
                width={1258}
                height={380}
                className="mt-10 w-full rounded-sm object-cover"
              />
            ) : null}
          </FadeIn>
          <div className="prose-article mt-12">
            <Body />
          </div>
          <div className="mt-16">
            <EmailCaptureForm
              source={`blog:${article.slug}`}
              heading="Get the next essay in your inbox"
              subheading="One piece a month on inbound, LinkedIn, and pipeline systems for advisory firms. No fluff, no spam."
              buttonLabel="Subscribe"
              successMessage="You're in. First essay lands soon."
            />
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
